import { z } from 'zod';
import { DAY_PATTERN, GUEST_ACCESS, isPlausibleDay, type GuestAccess } from '@werft/domain';
import { emailSchema, opIdSchema, patchOf, uuidSchema } from './common.js';

/**
 * Gäste (P15, ADR 0018): eingeladen auf ein ganzes Projekt oder einzelne Bereiche (samt Unterbereichen), mit einer
 * Stufe Ansehen / Kommentieren / Bearbeiten. Verwaltet von der Projektleitung.
 */
export const guestAccessSchema = z.enum(GUEST_ACCESS);

export interface GuestDto {
  userId: string;
  name: string;
  email: string;
  access: GuestAccess;
  /** ganzes Projekt (inkl. „Allgemein“ und Chat) – sonst nur `areaIds` */
  wholeProject: boolean;
  areaIds: string[];
  /** P21: einzelne Aufgaben (auch außerhalb der Bereiche, nie aus „nur fürs Team“) */
  tasks: { id: string; title: string }[];
  /** P21/ADR 0063: innerhalb der Freigabe nur eigene Aufgaben (zugewiesen, ausdrücklich freigegeben) */
  onlyAssigned: boolean;
  /** ADR 0063: bei „nur eigene Aufgaben“ auch selbst angelegte */
  ownCreated: boolean;
  /** ADR 0063: Material der Bereiche (Dateien, Notizen, Gespräche) sichtbar – aus = nur Aufgaben samt Anhängen */
  material: boolean;
  /** W09: gilt bis einschließlich diesem Tag (null = unbefristet) – der Server beendet den Zugang danach */
  until: string | null;
  version: number;
}

/** W10: ruhender Gastzugang – abgelaufen oder durch die Notfallsperre angehalten */
export interface HeldGuestDto {
  userId: string;
  name: string;
  email: string;
  reason: 'expired' | 'lock' | 'links';
  access: GuestAccess;
  wholeProject: boolean;
  until: string | null;
  heldAt: string;
}

export interface GuestInvitationDto {
  id: string;
  email: string;
  access: GuestAccess;
  wholeProject: boolean;
  areaIds: string[];
  taskIds: string[];
  onlyAssigned: boolean;
  ownCreated: boolean;
  material: boolean;
  until: string | null;
  expiresAt: string;
}

export interface GuestsDto {
  guests: GuestDto[];
  invitations: GuestInvitationDto[];
  /** W10: abgelaufene bzw. gesperrte Zugänge (verlängern/wieder öffnen) */
  held: HeldGuestDto[];
}

/** Ergebnis: bestehende Gäste des Workspaces kommen sofort dazu, alle anderen bekommen eine Einladung */
export interface InviteGuestResultDto {
  guest?: GuestDto;
  invitation?: GuestInvitationDto;
  /** nur in der ersten Antwort (wie bei Workspace-Einladungen) */
  acceptUrl?: string;
}

export const guestUntilSchema = z.string().regex(DAY_PATTERN).refine(isPlausibleDay, 'Ungültiges Datum').nullable();
const scope = { wholeProject: z.boolean(), areaIds: z.array(uuidSchema).max(20), taskIds: z.array(uuidSchema).max(100), onlyAssigned: z.boolean(), ownCreated: z.boolean(), material: z.boolean() };
const SCOPE_MESSAGE = 'Bitte das ganze Projekt, mindestens einen Bereich oder eine Aufgabe freigeben.';

export const inviteGuestInput = z
  .object({
    opId: opIdSchema,
    email: emailSchema,
    access: guestAccessSchema,
    wholeProject: scope.wholeProject,
    areaIds: scope.areaIds.default([]),
    taskIds: scope.taskIds.default([]),
    onlyAssigned: scope.onlyAssigned.default(false),
    ownCreated: scope.ownCreated.default(false),
    material: scope.material.default(true),
    until: guestUntilSchema.default(null),
  })
  .refine((x) => x.wholeProject || x.areaIds.length > 0 || x.taskIds.length > 0, { message: SCOPE_MESSAGE, path: ['areaIds'] });

export const updateGuestInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ access: guestAccessSchema, wholeProject: scope.wholeProject, areaIds: scope.areaIds, taskIds: scope.taskIds, onlyAssigned: scope.onlyAssigned, ownCreated: scope.ownCreated, material: scope.material, until: guestUntilSchema }),
});

/**
 * ADR 0063 (G17): Zugangsprofil eines Mitglieds – Stufe und Umfang wie bei Gästen. Ohne Profil (null) arbeitet ein
 * Mitglied wie bisher (Bearbeiten, alle Aufgaben seiner Bereiche, Material sichtbar).
 */
/** Nachprüfung 0.111 F02 (ADR 0113): `intake` – Vorgänge im Eingang: alle sichtbaren oder nur mir zugewiesene (fehlt = alle) */
export const INTAKE_SCOPES = ['all', 'assigned'] as const;
export const memberProfileSchema = z.object({ access: guestAccessSchema, onlyAssigned: z.boolean(), ownCreated: z.boolean(), material: z.boolean(), intake: z.enum(INTAKE_SCOPES).optional() });
export type MemberProfile = z.infer<typeof memberProfileSchema>;

/** W10: abgelaufenen Zugang verlängern bzw. ruhenden wieder öffnen (neues Ende oder unbefristet) */
export const restoreGuestInput = z.object({ opId: opIdSchema, until: guestUntilSchema });

/** P21: eine Aufgabe für einen bestehenden Gast freigeben oder die Freigabe entziehen (aus den Aufgabendetails) */
export const grantTaskInput = z.object({ opId: opIdSchema, on: z.boolean() });

/** P21: wer nicht mehr im Projekt ist – Name als Schnappschuss (Kommentare, Beiträge, Verlauf) */
export interface FormerMemberDto {
  userId: string;
  name: string;
  guest: boolean;
}
